import React from 'react';
import { Dispatch } from '../../store/store';
import { connect } from 'react-redux';
import CheckboxCircleControl from '../CheckboxCircleControl/CheckboxCircleControl';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import CycleSettings from '../../enums/cycle-setting.enum';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import { IValueWithTime } from '../../types/channel-types';
import { CycleSettingAction } from '../../enums/action';

type Props = {
  mode: IValueWithTime<CycleSettings>;
  cycleId: string;
  onSubmit?: (value: CycleSettings) => void;
};
type DispatchProps = ReturnType<typeof mapDispatch>;
type ComponentProps = DispatchProps & Props;

type FormValues = {
  mode: CycleSettings;
};

const ACTION_COMMAND: Record<CycleSettings, CycleSettingAction> = {
  [CycleSettings.PercentageTimer]: CycleSettingAction.SET_MODE_TO_PERCENTAGE_TIMER,
  [CycleSettings.PostWeld]: CycleSettingAction.SET_MODE_TO_POST_WELD,
  [CycleSettings.PreHeat]: CycleSettingAction.SET_MODE_TO_PREHEAT,
  [CycleSettings.Recorder]: CycleSettingAction.SET_MODE_TO_RECORDER,
};
const CycleSettingsMode: React.FC<ComponentProps> = ({ mode, updateSettings, updateDbSettings, cycleId, onSubmit }) => {
  const onSubmitHandler = async (values: FormValues) => {
    const selectedMode: CycleSettings = values.mode;
    const payload = { mode: selectedMode }
    await updateDbSettings({ id: cycleId, payload: { cycleDbSettings: payload } });
    await updateSettings({ id: cycleId, payload: { action: ACTION_COMMAND[selectedMode] } });
    if (onSubmit) {
      onSubmit(selectedMode);
    }
  };

  const formik = useFormik<FormValues>({
    initialValues: { mode: mode?.value },
    validationSchema: object({
      mode: string().required('Mode is required').oneOf(Object.values(CycleSettings)),
    }),
    onSubmit: onSubmitHandler,
  });

  const isDisabledBtn = Boolean(formik.values.mode === mode.value);

  const cycleSettingsValues = Object.values(CycleSettings).map(
    (value) => value.charAt(0).toUpperCase() + value.slice(1),
  );

  const changeMode = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;

    formik.setFieldValue(name, value.toLocaleLowerCase());
  };

  return (
    <form className="c-cycle-settings-mode flex flex-row p-7" onSubmit={formik.handleSubmit}>
      <div className='flex w-full'>
        <div className="flex flex-col">
          <div className="flex flex-row items-center justify-between pb-4">
            <p className="text-title-sm font-semibold">Mode</p>
            <InteractiveButton disabled={isDisabledBtn} text="Update" submit isLoading={formik.isSubmitting} />
          </div>
          <div className="flex flex-col max-lg:gap-y-2 lg:flex-row">
            {cycleSettingsValues.map((cycleMode) => (
              <CheckboxCircleControl
                key={cycleMode}
                name="mode"
                className="font-semibold"
                label={cycleMode}
                value={cycleMode}
                checked={formik.values.mode.toLowerCase() === cycleMode.toLowerCase()}
                onChange={changeMode}
              />
            ))}
          </div>
        </div>
      </div>
    </form>
  );
};

const mapDispatch = (dispatch: Dispatch) => ({
  updateSettings: dispatch.cycles.updateSettings,
  updateDbSettings: dispatch.cycles.updateDbSettings,
});

export default connect<null, DispatchProps, Props>(null, mapDispatch)(CycleSettingsMode);
